import { NumberInput } from '@carbon/react';

/** Props for {@link PctInput}. */
export interface PctInputProps {
  /** DOM id (must be unique on the page). */
  id: string;
  /** Visible field label (e.g. "WI %"). */
  label: string;
  /**
   * Accessible name override. The visible `label` alone can repeat across a form
   * (e.g. BPO "WI %" and APO "WI %"), leaving them indistinguishable to a screen
   * reader; pass a phase-qualified name here (e.g. "Before payout WI %"). It
   * contains the visible label text, satisfying WCAG 2.5.3 (Label in Name).
   */
  ariaLabel?: string;
  /**
   * Current value (percent). `null`/absent renders the field EMPTY rather than 0 —
   * a term we could not derive (a tract whose wells disagree on our interest) must
   * not read as "we own none" (INS-10673).
   */
  value: number | null;
  /** Called with the clamped next value on change. */
  onChange: (value: number) => void;
  /** Upper clamp (default 100). */
  max?: number;
  /** Step increment (default 0.5). */
  step?: number;
  /**
   * Renders the field read-only. Used for a term the calculator DERIVES rather than
   * takes (a UMI carried election's before-payout pair), so the box can show the
   * real figure without inviting an edit that would be discarded.
   */
  disabled?: boolean;
  /** Small print under the field — e.g. the basis a derived figure came from. */
  helperText?: string;
}

/**
 * Labeled percent entry for the Interest Modeling carry terms (BPO/APO WI & NRI).
 * A thin wrapper over Carbon's `NumberInput` (size sm) that CLAMPS the value to
 * `[0, max]` — Carbon flags an out-of-range value invalid but does not clamp it,
 * and the working config must never hold a percent outside its bounds. A
 * non-numeric change (mid-edit empty / partial) is ignored, leaving the last
 * good value. A `null` value renders the field empty (see {@link PctInputProps.value}).
 *
 * @param {PctInputProps} props - The field id/label, value, change handler, and bounds.
 * @return {JSX.Element} The percent input.
 */
export function PctInput({
  id,
  label,
  ariaLabel,
  value,
  onChange,
  max = 100,
  step = 0.5,
  disabled = false,
  helperText,
}: PctInputProps) {
  return (
    <NumberInput
      id={id}
      label={label}
      aria-label={ariaLabel}
      size="sm"
      disabled={disabled}
      helperText={helperText}
      min={0}
      max={max}
      step={step}
      value={value ?? ''}
      onChange={(_event, { value: next }) => {
        const parsed =
          typeof next === 'number' ? next : parseFloat(String(next));
        if (Number.isNaN(parsed)) {
          return;
        }
        onChange(Math.max(0, Math.min(max, parsed)));
      }}
    />
  );
}
